import { useMemo } from "react";
import { ActivityIndicator, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { densityTrend, formatWeight } from "@jackedlogs/shared";

import { LineChart } from "../charts/LineChart";
import { Icon } from "../Icon/Icon";
import { PanelTitleRow } from "./InfoPill";
import { DEFAULT_WINDOW_DAYS } from "./progressWindow";
import { makeTierTwoStyles } from "./tierTwoPanelStyles";
import type { ProgressDeps } from "./types";
import { useSessionResultsFromDeps } from "./useSessionResults";

// Session-density (kg/min) over the last 90 days.
export function DensityTrendPanel(deps: ProgressDeps) {
  const { theme, units } = deps;
  const windowDays = deps.windowDays ?? DEFAULT_WINDOW_DAYS;
  const { t, i18n } = useTranslation();
  const { results, error } = useSessionResultsFromDeps(deps);
  const styles = useMemo(() => makeTierTwoStyles(theme), [theme]);
  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { day: "2-digit", month: "short" }),
    [i18n.language],
  );
  const period = t("progress.period.days", {
    defaultValue: "{{days}} d",
    days: windowDays,
  });

  const points = useMemo(() => {
    if (!results) return null;
    return densityTrend(results, Date.now(), windowDays);
  }, [results, windowDays]);

  const densityTitle = t("mobile.progress.density.title", {
    defaultValue: "Density",
  });
  const densityHelp = t("mobile.progress.density.help", {
    defaultValue:
      'Volume divided by session duration, kg/min. Reads as "how dense was that session" — going up means you work the same load in less time (better conditioning) or more load in the same time (better strength). A sudden drop without changing the program often means fatigue: the rests stretched even if you didn\'t plan them.',
  });

  if (error) {
    return (
      <View style={[styles.card, styles.cardError]}>
        <PanelTitleRow
          theme={theme}
          title={densityTitle}
          body={densityHelp}
          period={period}
        />
        <Text style={styles.errorText}>
          {t("mobile.progress.loadFailed", { defaultValue: "Couldn’t load" })}
        </Text>
      </View>
    );
  }

  if (points === null) {
    return (
      <View style={styles.card}>
        <PanelTitleRow
          theme={theme}
          title={densityTitle}
          body={densityHelp}
          period={period}
        />
        <ActivityIndicator color={theme.palette.brand500} />
      </View>
    );
  }

  if (points.length < 3) {
    return (
      <View style={styles.card}>
        <PanelTitleRow
          theme={theme}
          title={densityTitle}
          body={densityHelp}
          period={period}
        />
        <Text style={styles.empty}>
          {t("mobile.progress.density.empty", {
            defaultValue: "Need 3+ timed sessions to chart density",
          })}
        </Text>
      </View>
    );
  }

  const latest = points[points.length - 1]!.kgPerMin;
  const first = points[0]!.kgPerMin;
  const deltaPct = first === 0 ? 0 : ((latest - first) / first) * 100;
  const trendIcon =
    deltaPct > 5 ? "trend-up" : deltaPct < -5 ? "trend-down" : "arrow-right";
  // Trend arrow = advisory judgement (not a brand action): up→complete green,
  // down→danger red, flat→neutral ink3. Off the brand per colour budget S2.1.
  const arrowColor =
    deltaPct > 5
      ? theme.palette.complete
      : deltaPct < -5
        ? theme.palette.danger
        : theme.palette.ink3;

  return (
    <View style={styles.card}>
      <PanelTitleRow
        theme={theme}
        title={densityTitle}
        body={densityHelp}
        period={period}
        right={
          <View style={styles.flagRow}>
            <Icon
              name={trendIcon}
              size={theme.typography.sizes.sm}
              color={arrowColor}
            />
            <Text style={[styles.densitySummary, { color: arrowColor }]}>
              {formatWeight(latest, units, i18n.language)}/min
            </Text>
          </View>
        }
      />
      <LineChart
        series={[
          {
            key: "density",
            variant: "primary",
            points: points.map((p) => ({
              x: p.atMs,
              y: p.kgPerMin,
              label: dateFmt.format(new Date(p.atMs)),
            })),
          },
        ]}
        yFormat={(n) => formatWeight(n, units, i18n.language)}
        theme={theme}
      />
    </View>
  );
}
